import { Injectable } from "@angular/core";
import { APIService } from "../../../api/api.service";
import {
  CalendarResponse,
  CancelSchedulePayload,
  EditSchedulePayload,
  ImportResult,
  ReadinessResult,
  ScheduleDetail,
} from "./models/schedule.model";
import { fileToBase64 } from "./schedule.utils";

/**
 * All patching-schedule API calls live here so components never build
 * URLs, headers or payloads themselves. Every method either resolves
 * with typed data or throws an Error whose message is safe to show.
 */
@Injectable({ providedIn: "root" })
export class PatchingService {
  constructor(private api: APIService) {}

  // TODO: point at the real endpoint that handles these actions
  private get url(): string {
    const prefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";
    return `${APIService.API_ENDPOINTV3}/${prefix}/operations/ec2-patching`;
  }

  /** Email of the logged-in user (sent as edited_by / cancelled_by). Adjust to your localStorage key. */
  get currentUserEmail(): string {
    return localStorage.getItem("email") || localStorage.getItem("userEmail") || "";
  }

  /** Name of the logged-in user. Adjust to the key your app stores it under. */
  get currentUser(): string {
    return localStorage.getItem("uName") || localStorage.getItem("name") || "";
  }

  private async call<T>(body: Record<string, unknown>): Promise<T> {
    let res: any;
    try {
      res = await this.api.postDataPromis(this.url, body, {
        Authorization: localStorage.getItem("t"),
      });
    } catch {
      throw new Error("Network error. Please check your connection and try again.");
    }
    const ok = res && (res.status == "ok" || res.status == "1" || res.s == "1");
    if (!ok) {
      throw new Error((res && (res.error || res.msg)) || "Request failed. Please try again.");
    }
    return res as T;
  }

  /** Calendar list. TODO: confirm the action name with the backend. */
  listSchedules(accountId?: string, customer?: string): Promise<CalendarResponse> {
    // TODO: confirm the backend filters list_schedules by account_id
    return this.call<CalendarResponse>({
      action: "list_schedules",
      ...(accountId ? { account_id: accountId } : {}),
      customer: customer
    });
  }

  async importSchedule(file: File, customer: string): Promise<ImportResult> {
    const file_base64 = await fileToBase64(file);
    return this.call<ImportResult>({
      action: "import_schedule",
      file_name: file.name,
      customer,
      file_base64,
    });
  }

  publishSchedules(importId: string, publishedBy: string): Promise<{ status: string }> {
    return this.call({
      action: "publish_schedules",
      import_id: importId,
      published_by: publishedBy,
    });
  }

  getSchedule(scheduleId: string): Promise<ScheduleDetail> {
    return this.call<ScheduleDetail>({ action: "get_schedule", schedule_id: scheduleId });
  }

  editSchedule(payload: EditSchedulePayload): Promise<{ status: string }> {
    return this.call({ action: "edit_schedule", ...payload });
  }

  cancelSchedule(payload: CancelSchedulePayload): Promise<{ status: string }> {
    return this.call({ action: "cancel_schedule", ...payload });
  }

  checkSchedule(scheduleId: string): Promise<ReadinessResult> {
  return this.call<ReadinessResult>({ action: "check_schedule", schedule_id: scheduleId });
}
}